import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Pencil, UserPlus, Send, Users, CreditCard } from 'lucide-react';
import Joyride from 'react-joyride';
import { WorkerDialog } from '@/components/pages/dialogs/WorkerDialog';
import getWorkersTourSteps from '@/components/tour/company/WorkersTourSteps';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import Pagination from '@/components/pages/shared/Pagination';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getTourStyles } from '@/components/tour/tourStyles';
import {
  useTrabajadoresDetallesEmpresa,
  type TrabajadorDetalles,
} from '@/components/hooks/useTrabajadorDetalles';
import WorkerSelfAvatar from '@/components/pages/shared/WorkerSelfAvatar';
import { useReenviarInvitacion } from '@/components/hooks/useInvitarTrabajador';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: 20, transition: { duration: 0.2 } },
};

// Estados de suscripción que cuentan como "con acceso vivo" para el badge.
const ESTADOS_ACTIVOS = ['trialing', 'active', 'past_due'];

const fmtFecha = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

// Devuelve { label, cls } del badge de acceso según el estado del
// trabajador (campos que ya trae vista_trabajadores_detalles). Prioridad:
// baja programada > incluido en plan > gratis por promo > facturando >
// sin contratar.
const getEstadoAcceso = (w: TrabajadorDetalles) => {
  if (w.cancelacion_programada) {
    const hasta = fmtFecha(w.fecha_fin_acceso);
    return {
      label: hasta ? `Baja programada · hasta ${hasta}` : 'Baja programada',
      cls: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    };
  }
  if (w.es_incluido_plan) {
    return {
      label: 'Incluido en el plan',
      cls: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    };
  }
  if (w.cubierto_por_promo && w.tiene_acceso) {
    const hasta = fmtFecha(w.fecha_fin_acceso);
    return {
      label: hasta ? `Gratis (promo) · hasta ${hasta}` : 'Gratis (promo)',
      cls: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    };
  }
  if (ESTADOS_ACTIVOS.includes(w.stripe_status ?? '')) {
    return {
      label: 'Facturando',
      cls: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    };
  }
  return {
    label: 'Sin contratar',
    cls: 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400',
  };
};

// El botón "Contratar" (ir a Suscripciones) solo tiene sentido para
// trabajadores ACTIVOS que aún no facturan: los que están gratis por
// promo (empezarán a pagar cuando acabe) o los que no tienen nada
// contratado. Los incluidos en cupo, facturando o con baja programada no
// lo muestran.
const puedeContratarse = (w: TrabajadorDetalles): boolean => {
  if (!w.activo) return false;
  const enPromo = !!(w.cubierto_por_promo && w.tiene_acceso);
  const sinContratar =
    !w.es_incluido_plan &&
    !w.cancelacion_programada &&
    !w.cubierto_por_promo &&
    !ESTADOS_ACTIVOS.includes(w.stripe_status ?? '');
  return enPromo || sinContratar;
};

const Workers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = window.innerWidth < 640;
  const [open, setOpen] = useState(false);
  const [editingWorker, setEditingWorker] = useState<any | null>(null);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [runTour, setRunTour] = useState(false);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);

  const empresaId = localStorage.getItem('id_empresa') || '';
  const currentUserEmail = localStorage.getItem('email') || '';
  const currentUserId = localStorage.getItem('id_usuario') || '';

  const steps = getWorkersTourSteps(isMobile);

  const {
    trabajadores: workers,
    refetch: refetchWorkers,
    loading,
  } = useTrabajadoresDetallesEmpresa(empresaId);

  const { reenviar } = useReenviarInvitacion();

  // El botón global de ayuda (arriba a la derecha) lanza el tour de esta
  // página. Se oculta mientras carga, que es cuando aún no hay tabla que
  // enseñar.
  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);

  const openCreateModal = () => {
    setEditingWorker(null);
    setOpen(true);
  };

  const openEditModal = (worker: any) => {
    setEditingWorker(worker);
    setOpen(true);
  };

  const openSelfRegisterModal = () => {
    setEditingWorker({
      _prefill: true,
      email: currentUserEmail,
      nombre_completo: '',
      intervalo_cita: 30,
      activo: true,
    });
    setOpen(true);
  };

  const resendInvitation = async (worker: any) => {
    setResendingEmail(worker.email);
    const { ok, error } = await reenviar(worker);
    if (ok) toast.success(`Invitación reenviada a ${worker.email}`);
    else toast.error(error ?? 'Error al reenviar la invitación');
    setResendingEmail(null);
  };

  // Lleva a la pantalla de Suscripciones anclando al trabajador concreto
  // (query param que Subscriptions lee para hacer scroll y resaltarlo).
  const irAContratar = (worker: TrabajadorDetalles) => {
    navigate(`/panel-empresa/subscriptions?trabajador=${worker.id_usuario}`);
  };

  const handleReload = async () => {
    await refetchWorkers();
    setFilter((f) => f + '');
    window.dispatchEvent(new CustomEvent('trabajadores-actualizados'));
  };

  const filtered = (workers || []).filter((w) => {
    const q = filter.toLowerCase();
    return (
      (w.nombre_completo?.toLowerCase?.() || '').includes(q) ||
      (w.email?.toLowerCase?.() || '').includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const currentData = filtered.slice((page - 1) * pageSize, page * pageSize);

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-8 pb-28 lg:pb-0"
    >
      {/* Header — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants}>
        <div className="tour-title flex items-center text-sm text-gray-600 dark:text-gray-400">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('workers.title')}
          </span>
        </div>
      </motion.div>

      {/* Tour */}
      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: t('workers.tour.back'),
          close: t('workers.tour.close'),
          last: t('workers.tour.last'),
          next: t('workers.tour.next'),
          skip: t('workers.tour.skip'),
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped')
            setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {/* Filtro + botón añadir */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col sm:flex-row gap-3 justify-between items-center"
      >
        <Input
          type="text"
          placeholder={t('workers.searchPlaceholder')}
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(1);
          }}
          className="tour-filter-input w-full sm:w-1/2 dark:bg-white/5 dark:border-white/10 dark:text-white"
        />

        {!loading && workers && workers.length > 0 && (
          <Button
            onClick={openCreateModal}
            className="rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] px-6 py-2 w-full sm:w-auto tour-add-btn transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" /> {t('workers.addBtn')}
          </Button>
        )}
      </motion.div>

      {open && (
        <WorkerDialog
          open={open}
          setOpen={setOpen}
          editingWorker={editingWorker}
          onSaved={handleReload}
        />
      )}

      <AnimatePresence>
        {loading ? (
          <motion.div
            variants={itemVariants}
            className="flex justify-center items-center h-32"
          >
            <motion.div
              variants={rowVariants}
              className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#103254]"
            />
          </motion.div>
        ) : !workers || workers.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center">
              <Users className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                {t('workers.noWorkers')}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">
                {t('workers.noWorkersText')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={openCreateModal}
                  className="rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] px-6 py-2 w-full sm:w-auto transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" /> {t('workers.addBtn')}
                </Button>

                {currentUserEmail && (
                  <Button
                    onClick={openSelfRegisterModal}
                    variant="outline"
                    className="rounded-lg font-medium px-6 py-2 border-[#103254] text-[#103254] dark:border-blue-400 dark:text-blue-400 hover:bg-[#103254]/5 dark:hover:bg-blue-400/10"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {t('workers.selfRegister')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center">
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                {t('workers.noResults')}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {t('workers.noResultsText')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Vista móvil - Tarjetas */}
            <div className="block lg:hidden space-y-4">
              {currentData.map((worker) => {
                const getIniciales = (nombre: string) =>
                  (nombre ?? '')
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((s) => s[0]?.toUpperCase())
                    .join('') || '?';

                const estadoAcceso = getEstadoAcceso(worker);

                return (
                  <motion.div
                    key={worker.id_usuario}
                    variants={rowVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <Card className={`rounded-xl ${GLASS_CARD}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center gap-3 flex-1">
                            {worker.id_usuario === currentUserId ? (
                              <WorkerSelfAvatar
                                userId={worker.id_usuario}
                                nombre={worker.nombre_completo}
                                foto={worker.foto}
                                sizeClass="h-12 w-12"
                                onChanged={handleReload}
                              />
                            ) : worker.foto ? (
                              <img
                                src={`${worker.foto}?t=${Date.now()}`}
                                alt={worker.nombre_completo}
                                className="h-12 w-12 rounded-full object-cover border border-gray-200 dark:border-white/10"
                              />
                            ) : (
                              <div className="h-12 w-12 rounded-full bg-[#103254]/10 dark:bg-blue-400/10 text-[#103254] dark:text-blue-400 flex items-center justify-center font-bold text-sm">
                                {getIniciales(worker.nombre_completo)}
                              </div>
                            )}

                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 dark:text-white text-base truncate">
                                {worker.nombre_completo}
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                {worker.email}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 ml-2">
                            {puedeContratarse(worker) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => irAContratar(worker)}
                                title="Contratar en Suscripciones"
                                className="text-[#288E7F] dark:text-emerald-400 hover:text-[#22786c] dark:hover:text-emerald-300 rounded-full hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                              >
                                <CreditCard className="w-4 h-4" />
                              </Button>
                            )}

                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditModal(worker)}
                              className="text-[#103254] dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-full tour-edit-btn-mobile hover:bg-gray-100 dark:hover:bg-white/10"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>

                            {!worker.confirmado && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => resendInvitation(worker)}
                                disabled={resendingEmail === worker.email}
                                title="Reenviar invitación"
                                className="text-amber-500 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 rounded-full tour-resend-btn-mobile hover:bg-amber-50 dark:hover:bg-amber-900/20"
                              >
                                <Send
                                  className={`w-4 h-4 ${
                                    resendingEmail === worker.email
                                      ? 'animate-pulse'
                                      : ''
                                  }`}
                                />
                              </Button>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-gray-100 dark:border-white/10">
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              {t('workers.cardInterval')}
                            </span>
                            <p className="font-medium text-gray-800 dark:text-white">
                              {t('workers.intervalUnit', {
                                n: worker.intervalo_cita,
                              })}
                            </p>
                          </div>

                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              Acceso
                            </span>
                            <p className="font-medium">
                              <span
                                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${estadoAcceso.cls}`}
                              >
                                {estadoAcceso.label}
                              </span>
                            </p>
                          </div>

                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              {t('workers.cardConfirmed')}
                            </span>
                            <p className="font-medium">
                              <span
                                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  worker.confirmado
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                }`}
                              >
                                {worker.confirmado
                                  ? t('workers.yes')
                                  : t('workers.no')}
                              </span>
                            </p>
                          </div>

                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              {t('workers.cardActive')}
                            </span>
                            <p className="font-medium">
                              <span
                                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  worker.activo
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                }`}
                              >
                                {worker.activo
                                  ? t('workers.yes')
                                  : t('workers.no')}
                              </span>
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {/* Vista desktop - TABLA */}
            <Card className={`hidden lg:block rounded-2xl ${GLASS_CARD}`}>
              <CardContent className="overflow-x-auto p-0">
                <table className="min-w-full table-auto text-sm">
                  <thead className="tour-table-head bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 uppercase text-xs sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        {t('workers.colPhoto')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('workers.colName')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('workers.colEmail')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('workers.colInterval')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        Acceso
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('workers.colConfirmed')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('workers.colActive')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('workers.colActions')}
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                    {currentData.map((worker, idx) => {
                      const getIniciales = (nombre: string) =>
                        (nombre ?? '')
                          .trim()
                          .split(/\s+/)
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((s) => s[0]?.toUpperCase())
                          .join('') || '?';

                      const estadoAcceso = getEstadoAcceso(worker);

                      return (
                        <motion.tr
                          key={worker.id_usuario}
                          variants={rowVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          className={
                            idx % 2 === 0
                              ? 'bg-white dark:bg-transparent'
                              : 'bg-gray-50 dark:bg-white/[0.03]'
                          }
                        >
                          <td className="px-4 py-3">
                            {worker.id_usuario === currentUserId ? (
                              <WorkerSelfAvatar
                                userId={worker.id_usuario}
                                nombre={worker.nombre_completo}
                                foto={worker.foto}
                                sizeClass="h-10 w-10"
                                onChanged={handleReload}
                              />
                            ) : worker.foto ? (
                              <img
                                src={`${worker.foto}?t=${Date.now()}`}
                                alt={worker.nombre_completo}
                                className="h-10 w-10 rounded-full object-cover border border-gray-200 dark:border-white/10"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-[#103254]/10 dark:bg-blue-400/10 text-[#103254] dark:text-blue-400 flex items-center justify-center font-bold text-xs">
                                {getIniciales(worker.nombre_completo)}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-3 font-medium text-gray-800 dark:text-white">
                            {worker.nombre_completo}
                          </td>

                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                            {worker.email}
                          </td>

                          <td className="px-4 py-3 text-gray-800 dark:text-white">
                            {t('workers.intervalUnit', {
                              n: worker.intervalo_cita,
                            })}
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${estadoAcceso.cls}`}
                            >
                              {estadoAcceso.label}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold tour-confirmado-badge ${
                                worker.confirmado
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                              }`}
                            >
                              {worker.confirmado
                                ? t('workers.yes')
                                : t('workers.no')}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                worker.activo
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                              }`}
                            >
                              {worker.activo
                                ? t('workers.yes')
                                : t('workers.no')}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {puedeContratarse(worker) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => irAContratar(worker)}
                                  title="Contratar en Suscripciones"
                                  className="text-[#288E7F] dark:text-emerald-400 hover:text-[#22786c] dark:hover:text-emerald-300 rounded-full hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                                >
                                  <CreditCard className="w-5 h-5" />
                                </Button>
                              )}

                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditModal(worker)}
                                className="text-[#103254] dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-full tour-edit-btn-desktop hover:bg-gray-100 dark:hover:bg-white/10"
                              >
                                <Pencil className="w-5 h-5" />
                              </Button>

                              {!worker.confirmado && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => resendInvitation(worker)}
                                  disabled={resendingEmail === worker.email}
                                  title="Reenviar invitación"
                                  className="text-amber-500 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 rounded-full tour-resend-btn-desktop hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                >
                                  <Send
                                    className={`w-4 h-4 ${
                                      resendingEmail === worker.email
                                        ? 'animate-pulse'
                                        : ''
                                    }`}
                                  />
                                </Button>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Paginación */}
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={filtered.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={changePageSize}
              itemLabel="trabajadores"
              className="tour-pagination"
            />
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Workers;